import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { SESSION_CONFIG, STORAGE_KEYS } from '../constants';

// Initial state
const initialState = {
  // Auth state
  isAuthenticated: false,
  user: null,
  token: null,

  // Session state
  sessionTimeout: null,
  lastActivity: Date.now(),

  // App state
  loading: false,
  error: null,
  theme: 'light',
  sidebarCollapsed: false,

  // Notifications
  notifications: [],
};

// Action types
export const ACTION_TYPES = {
  // Auth actions
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',

  // Session actions
  UPDATE_ACTIVITY: 'UPDATE_ACTIVITY',
  SESSION_TIMEOUT_WARNING: 'SESSION_TIMEOUT_WARNING',
  SESSION_EXPIRED: 'SESSION_EXPIRED',

  // App actions
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  SET_THEME: 'SET_THEME',

  // Notifications
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  CLEAR_NOTIFICATIONS: 'CLEAR_NOTIFICATIONS',
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
      };

    case ACTION_TYPES.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
      };

    case ACTION_TYPES.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    case ACTION_TYPES.UPDATE_ACTIVITY:
      return {
        ...state,
        lastActivity: Date.now(),
      };

    case ACTION_TYPES.SESSION_TIMEOUT_WARNING:
      return {
        ...state,
        sessionTimeout: 'warning',
      };

    case ACTION_TYPES.SESSION_EXPIRED:
      return {
        ...state,
        sessionTimeout: 'expired',
        isAuthenticated: false,
        user: null,
        token: null,
      };

    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ACTION_TYPES.TOGGLE_SIDEBAR:
      return {
        ...state,
        sidebarCollapsed: !state.sidebarCollapsed,
      };

    case ACTION_TYPES.SET_THEME:
      return {
        ...state,
        theme: action.payload,
      };

    case ACTION_TYPES.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };

    case ACTION_TYPES.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload
        ),
      };

    case ACTION_TYPES.CLEAR_NOTIFICATIONS:
      return {
        ...state,
        notifications: [],
      };

    default:
      return state;
  }
};

// Context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Initialize app state from localStorage
  useEffect(() => {
    const initializeApp = () => {
      try {
        // Check for existing auth token
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);

        if (token && userData) {
          dispatch({
            type: ACTION_TYPES.LOGIN_SUCCESS,
            payload: {
              token,
              user: JSON.parse(userData),
            },
          });
        }

        // Load theme preference
        const theme = localStorage.getItem(STORAGE_KEYS.THEME) || 'light';
        dispatch({
          type: ACTION_TYPES.SET_THEME,
          payload: theme,
        });

        // Load sidebar preference
        const sidebarCollapsed =
          localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED) === 'true';
        if (sidebarCollapsed) {
          dispatch({
            type: ACTION_TYPES.TOGGLE_SIDEBAR,
          });
        }
      } catch (error) {
        console.error('Error initializing app:', error);
        dispatch({
          type: ACTION_TYPES.SET_ERROR,
          payload: 'Failed to initialize application',
        });
      }
    };

    initializeApp();
  }, []);

  // Session activity tracking
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const handleActivity = () => {
      dispatch({ type: ACTION_TYPES.UPDATE_ACTIVITY });
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
    };

    // Add activity listeners
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup
    return () => {
      SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [state.isAuthenticated]);

  // Context value
  const value = {
    state,
    dispatch,

    // Auth helpers
    login: (userData, token) => {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
      dispatch({
        type: ACTION_TYPES.LOGIN_SUCCESS,
        payload: { user: userData, token },
      });
    },

    logout: () => {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
      dispatch({ type: ACTION_TYPES.LOGOUT });
    },

    // Notification helpers
    addNotification: notification => {
      const id = Date.now().toString();
      dispatch({
        type: ACTION_TYPES.ADD_NOTIFICATION,
        payload: { id, ...notification },
      });

      // Auto-remove after delay
      if (notification.autoRemove !== false) {
        setTimeout(() => {
          dispatch({
            type: ACTION_TYPES.REMOVE_NOTIFICATION,
            payload: id,
          });
        }, notification.duration || 5000);
      }
    },

    removeNotification: id => {
      dispatch({
        type: ACTION_TYPES.REMOVE_NOTIFICATION,
        payload: id,
      });
    },

    // Theme helpers
    setTheme: theme => {
      localStorage.setItem(STORAGE_KEYS.THEME, theme);
      dispatch({
        type: ACTION_TYPES.SET_THEME,
        payload: theme,
      });
    },

    // Sidebar helpers
    toggleSidebar: () => {
      const newState = !state.sidebarCollapsed;
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, newState.toString());
      dispatch({ type: ACTION_TYPES.TOGGLE_SIDEBAR });
    },
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Custom hook
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
